import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Trash2, Plus, Save, ArrowLeft, Upload, X, ChevronDown, Smile, Image as ImageIcon,
} from "lucide-react";
import api from "../api/axios";

const API_PATH = "/api/border-tax/ui";
const emptyService = (order) => ({
  title: "",
  subtitle: "",
  route: "",
  icon: "📄",
  iconImage: { url: "", id: "" },
  iconMode: "emoji",
  iconBackground: "#EF4444",
  badge: "",
  badgeColor: "#22C55E",
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  isNew: false,
  isPopular: false,
  isEnabled: true,
  order,
});

const BorderTaxUi = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("general");
  const [openServiceIdx, setOpenServiceIdx] = useState(null);

  // General Fields
  const [pageTitle, setPageTitle] = useState("All Tax Pay");
  const [pageDescription, setPageDescription] = useState("Easy tax payments for your vehicle by Taxi Safar");
  const [badgeText, setBadgeText] = useState("24/7 Service Available");
  const [badgeIcon, setBadgeIcon] = useState("");
  const [footerText, setFooterText] = useState("Secure • Fast • Verified by Taxi Safar");
  const [isActive, setIsActive] = useState(true);

  // Hero
  const [heroImage, setHeroImage] = useState(null);
  const [heroPreview, setHeroPreview] = useState("");

  // Services
  const [services, setServices] = useState([]);
  const [serviceIconFiles, setServiceIconFiles] = useState({});

  const fetchUi = async () => {
    try {
      setLoading(true);
      const res = await api.get("/border-tax/ui");
      const data = res.data.data || {};

      setPageTitle(data.pageTitle || "All Tax Pay");
      setPageDescription(data.pageDescription || "");
      setBadgeText(data.badgeText || "");
      setBadgeIcon(data.badgeIcon || "");
      setFooterText(data.footerText || "");
      setIsActive(data.isActive ?? true);
      setHeroPreview(data.heroBgImage?.url || "");

      const loadedServices = data.services?.length
        ? data.services
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((s) => ({
              ...s,
              iconImage: s.iconImage || { url: "", id: "" },
              iconMode: s.iconImage?.url ? "image" : "emoji",
            }))
        : [emptyService(0)];

      setServices(loadedServices);
      setServiceIconFiles({});
    } catch (err) {
      Swal.fire("Error", "Failed to load UI data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUi();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();

    formData.append("pageTitle", pageTitle);
    formData.append("pageDescription", pageDescription);
    formData.append("badgeText", badgeText);
    formData.append("badgeIcon", badgeIcon);
    formData.append("footerText", footerText);
    formData.append("isActive", isActive);

    if (heroImage) formData.append("heroBgImage", heroImage);

    services.forEach((_, i) => {
      if (serviceIconFiles[i]) {
        formData.append(`serviceIcon_${i}`, serviceIconFiles[i]);
      }
    });

    formData.append("services", JSON.stringify(services));

    try {
      await api.post("/border-tax/ui", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire("Success!", "UI Updated Successfully", "success");
      fetchUi();
    } catch (err) {
      console.log(err)
      Swal.fire("Error", err.response?.data?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteServiceById = async (serviceId) => {
    if (!serviceId) return;
    try {
      await api.delete(`/border-tax/ui/service/${serviceId}`);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteHeroImageApi = async () => {
    try {
      await api.delete("/border-tax/ui/hero-image");
    } catch (err) {
      console.error(err);
    }
  };

  const handleHeroChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImage(file);
      setHeroPreview(URL.createObjectURL(file));
    }
  };

  const removeHero = () => {
    setHeroImage(null);
    setHeroPreview("");
    deleteHeroImageApi();
  };

  const addService = () => {
    setServices([...services, emptyService(services.length)]);
    setOpenServiceIdx(services.length);
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const removeService = (index) => {
    const service = services[index];
    if (service?._id) deleteServiceById(service._id);

    setServices(services.filter((_, i) => i !== index));
    const files = { ...serviceIconFiles };
    delete files[index];
    setServiceIconFiles(files);
    if (openServiceIdx === index) setOpenServiceIdx(null);
  };

  const setServiceIconMode = (index, mode) => {
    updateService(index, "iconMode", mode);
    if (mode === "emoji") {
      const files = { ...serviceIconFiles };
      delete files[index];
      setServiceIconFiles(files);
      updateService(index, "iconImage", { url: "", id: "" });
    }
  };

  const handleServiceIconFile = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setServiceIconFiles({ ...serviceIconFiles, [index]: file });
    updateService(index, "iconImage", { url: URL.createObjectURL(file), id: "" });
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? "" : section);
  };

  const toggleServiceOpen = (index) => {
    setOpenServiceIdx(openServiceIdx === index ? null : index);
  };

  if (loading) return <div className="flex items-center justify-center h-screen text-sm">Loading Admin Panel...</div>;

  return (
    <div className="min-h-screen bg-gray-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-sm"
          >
            <ArrowLeft size={18} /> Back
          </button>
          <h1 className="text-lg sm:text-xl font-medium text-gray-900">All Tax Pay - UI Editor</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-70"
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ==================== MOBILE PREVIEW ==================== */}
          <div>
            <h2 className="text-sm font-medium mb-3 text-center text-gray-700">Mobile Preview</h2>
            <div className="flex justify-center">
              <div className="w-full max-w-[380px] bg-white rounded-[2.5rem] shadow-xl overflow-hidden border-8 border-gray-900 relative">
                <div className="h-10 bg-black flex items-center justify-center text-white text-[11px]">
                  TaxiSafar
                </div>

                <div className="h-[620px] overflow-y-auto bg-white">
                  {/* Header */}
                  <div className="px-4 pt-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-medium">S</div>
                      <span className="font-medium text-base text-red-600">TaxiSafar</span>
                    </div>
                    <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-sm">🛎️</div>
                  </div>

                  {/* Hero Section */}
                  <div className="px-5 pt-5 pb-8 relative bg-gradient-to-br from-red-50 to-white">
                    <h1 className="text-2xl font-medium leading-tight">{pageTitle}</h1>
                    <p className="text-gray-600 text-sm mt-2 pr-12">{pageDescription}</p>

                    <div className="mt-3 inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs px-3 py-1 rounded-2xl">
                      <span>{badgeIcon || "✅"}</span> {badgeText}
                    </div>

                    {heroPreview && (
                      <img
                        src={heroPreview}
                        alt="Hero"
                        className="absolute -bottom-6 right-4 w-36 drop-shadow-2xl"
                      />
                    )}
                  </div>

                  {/* Services */}
                  <div className="px-5 space-y-3 pt-4">
                    {services
                      .filter((s) => s.isEnabled)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((service, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 border border-gray-200 rounded-2xl p-3 hover:shadow-sm transition-all"
                          style={{
                            backgroundColor: service.backgroundColor,
                            color: service.textColor,
                          }}
                        >
                          <div
                            className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-2xl shadow-sm overflow-hidden"
                            style={{ backgroundColor: service.iconBackground }}
                          >
                            {service.iconMode === "image" && service.iconImage?.url ? (
                              <img
                                src={service.iconImage.url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              service.icon || "📄"
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm leading-tight">{service.title}</h4>
                              {service.isNew && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-orange-500 text-white rounded">NEW</span>
                              )}
                              {service.isPopular && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-purple-500 text-white rounded">POPULAR</span>
                              )}
                            </div>
                            <p className="text-xs mt-0.5 opacity-75 line-clamp-2">{service.subtitle}</p>
                          </div>

                          {service.badge && (
                            <div
                              className="text-[10px] px-2 py-1 rounded-full font-medium"
                              style={{ backgroundColor: service.badgeColor + "20", color: service.badgeColor }}
                            >
                              {service.badge}
                            </div>
                          )}

                          <div className="text-gray-400 text-xl">›</div>
                        </div>
                      ))}
                  </div>

                  {/* Footer Button */}
                  <div className="px-5 mt-8">
                    <button className="w-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-3.5 rounded-2xl shadow-sm">
                      Download All Tax PDF
                    </button>
                  </div>

                  {/* Footer Text */}
                  <div className="mt-8 px-5 pb-8">
                    <div className="bg-gray-100 text-center py-3 rounded-2xl text-xs flex items-center justify-center gap-1.5 text-gray-600">
                      <span>✅</span> {footerText}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== ADMIN FORM ==================== */}
          <div className="space-y-4">
            {/* General Settings */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection("general")}
                className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium border-b"
              >
                General Settings
                <ChevronDown size={16} className={`transition-transform ${openSection === "general" ? "rotate-180" : ""}`} />
              </button>
              {openSection === "general" && (
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Page Title</label>
                    <input type="text" value={pageTitle} onChange={(e) => setPageTitle(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Page Description</label>
                    <textarea value={pageDescription} onChange={(e) => setPageDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Badge Text</label>
                    <input type="text" value={badgeText} onChange={(e) => setBadgeText(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Badge Icon (Emoji)</label>
                    <input type="text" value={badgeIcon} onChange={(e) => setBadgeIcon(e.target.value)} placeholder="✅" className="w-full px-3 py-2 border rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Footer Text</label>
                    <input type="text" value={footerText} onChange={(e) => setFooterText(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-red-600" />
                    Page is Active
                  </label>
                </div>
              )}
            </div>

            {/* Hero Image */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection("hero")}
                className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium border-b"
              >
                Hero Image
                <ChevronDown size={16} className={`transition-transform ${openSection === "hero" ? "rotate-180" : ""}`} />
              </button>
              {openSection === "hero" && (
                <div className="p-5">
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center">
                    {heroPreview ? (
                      <div className="relative mx-auto w-40">
                        <img src={heroPreview} alt="Hero" className="rounded-xl shadow" />
                        <button onClick={removeHero} className="absolute -top-2 -right-2 bg-red-600 text-white p-1 rounded-full">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="mx-auto text-gray-400" size={40} />
                        <p className="mt-2 text-sm text-gray-600">Upload Hero Background Image</p>
                        <input type="file" accept="image/*" className="hidden" onChange={handleHeroChange} />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Services */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection("services")}
                className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium border-b"
              >
                Services ({services.length})
                <ChevronDown size={16} className={`transition-transform ${openSection === "services" ? "rotate-180" : ""}`} />
              </button>

              {openSection === "services" && (
                <div className="p-5">
                  <div className="flex justify-end mb-4">
                    <button onClick={addService} className="flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm hover:bg-black">
                      <Plus size={16} /> Add Service
                    </button>
                  </div>

                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    {services.map((service, index) => (
                      <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden">
                        <div
                          onClick={() => toggleServiceOpen(index)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                        >
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                            style={{ backgroundColor: service.iconBackground }}
                          >
                            {service.iconMode === "image" && service.iconImage?.url ? (
                              <img src={service.iconImage.url} alt="" className="w-full h-full object-cover rounded-xl" />
                            ) : (
                              service.icon
                            )}
                          </div>
                          <span className="flex-1 font-medium text-sm">{service.title || "Untitled"}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); removeService(index); }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={18} />
                          </button>
                          <ChevronDown size={16} className={`transition ${openServiceIdx === index ? "rotate-180" : ""}`} />
                        </div>

                        {openServiceIdx === index && (
                          <div className="p-4 border-t space-y-4">
                            {/* Basic Info */}
                            <input type="text" placeholder="Title" value={service.title} onChange={(e) => updateService(index, "title", e.target.value)} className="w-full border rounded-xl px-3 py-2" />
                            <input type="text" placeholder="Subtitle" value={service.subtitle} onChange={(e) => updateService(index, "subtitle", e.target.value)} className="w-full border rounded-xl px-3 py-2" />
                            <input type="text" placeholder="Route (/border-tax)" value={service.route} onChange={(e) => updateService(index, "route", e.target.value)} className="w-full border rounded-xl px-3 py-2" />

                            {/* Icon */}
                            <div>
                              <label className="block text-xs mb-2">Icon Type</label>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => setServiceIconMode(index, "emoji")} className={`flex-1 py-2 rounded-xl text-sm ${service.iconMode === "emoji" ? "bg-red-600 text-white" : "border"}`}>Emoji</button>
                                <button type="button" onClick={() => setServiceIconMode(index, "image")} className={`flex-1 py-2 rounded-xl text-sm ${service.iconMode === "image" ? "bg-red-600 text-white" : "border"}`}>Image</button>
                              </div>
                            </div>

                            {service.iconMode === "emoji" ? (
                              <input type="text" placeholder="Emoji" value={service.icon} onChange={(e) => updateService(index, "icon", e.target.value)} className="w-full border rounded-xl px-3 py-2" />
                            ) : (
                              <div>
                                {service.iconImage?.url ? (
                                  <div className="relative inline-block">
                                    <img src={service.iconImage.url} alt="" className="w-16 h-16 object-cover rounded-xl" />
                                    <button type="button" onClick={() => updateService(index, "iconImage", { url: "", id: "" })} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-1">
                                      <X size={12} />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer block">
                                    <Upload className="mx-auto" />
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleServiceIconFile(index, e)} />
                                  </label>
                                )}
                              </div>
                            )}

                            {/* Colors */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs block mb-1">Icon BG</label>
                                <input type="color" value={service.iconBackground} onChange={(e) => updateService(index, "iconBackground", e.target.value)} className="w-full h-10 border rounded-lg" />
                              </div>
                              <div>
                                <label className="text-xs block mb-1">Card BG</label>
                                <input type="color" value={service.backgroundColor} onChange={(e) => updateService(index, "backgroundColor", e.target.value)} className="w-full h-10 border rounded-lg" />
                              </div>
                              <div>
                                <label className="text-xs block mb-1">Text Color</label>
                                <input type="color" value={service.textColor} onChange={(e) => updateService(index, "textColor", e.target.value)} className="w-full h-10 border rounded-lg" />
                              </div>
                              <div>
                                <label className="text-xs block mb-1">Badge Color</label>
                                <input type="color" value={service.badgeColor} onChange={(e) => updateService(index, "badgeColor", e.target.value)} className="w-full h-10 border rounded-lg" />
                              </div>
                            </div>

                            {/* Badges & Flags */}
                            <div>
                              <label className="block text-xs mb-1">Service Badge Text</label>
                              <input type="text" value={service.badge} onChange={(e) => updateService(index, "badge", e.target.value)} placeholder="e.g. Best" className="w-full border rounded-xl px-3 py-2" />
                            </div>

                            <div className="flex gap-6">
                              <label className="flex items-center gap-2">
                                <input type="checkbox" checked={service.isNew} onChange={(e) => updateService(index, "isNew", e.target.checked)} />
                                <span className="text-sm">Is New</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" checked={service.isPopular} onChange={(e) => updateService(index, "isPopular", e.target.checked)} />
                                <span className="text-sm">Is Popular</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" checked={service.isEnabled} onChange={(e) => updateService(index, "isEnabled", e.target.checked)} />
                                <span className="text-sm">Enabled</span>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorderTaxUi;